import { createRestClient } from '@superbet-group/web.lib.rest';
import {
    IResponse,
    MalformedDataError,
    instanceGuard,
} from '@superbet-group/web.lib.utilities';
import {
    isEmpty as _isEmpty,
    keyBy as _keyBy,
} from 'lodash';

import { logger } from '@app/logger';
import { defaultHttpTimeoutMs } from '@core/constants';
import { mapCategories } from '@core/mappers/struct/CategoryMapper';
import { mapOddTypes } from '@core/mappers/struct/OddTypeMapper';
import { mapSports } from '@core/mappers/struct/SportMapper';
import { mapTournaments } from '@core/mappers/struct/TournamentMapper';
import { StructDto } from '@core/mappers/struct/dtos/StructDto';
import Struct from '@core/models/struct/Struct';

import RestService from './_private/RestService';
import Singleton from '../common/Singleton';
import SingletonError from '../common/errors/SingletonError';
import { mapMarketTrees } from '@core/mappers/struct/MarketsNodeMapper';
import { featureFlagService } from '@superbet-group/web.lib.flags';
import { getOfferHost } from './helpers';
import { FeatureToggleService } from '@superbet-group/web.lib.feature-toggle-service';

class StructService {
    private restService: RestService;
    private featureFlagService: FeatureToggleService;
    private static instance?: StructService;

    private constructor() {
        if (StructService.instance) {
            throw new SingletonError(this.constructor.name);
        }
        this.restService = new RestService(createRestClient(getOfferHost(), defaultHttpTimeoutMs));
        this.featureFlagService = featureFlagService;
    }

    public static getInstance(): StructService {
        return instanceGuard(StructService.instance)!;
    }

    public static createInstance() {
        if (!StructService.instance) {
            StructService.instance = new StructService();
        }
        return StructService.instance;
    }

    public static clearInstance() {
        if (process.env.NODE_ENV !== 'test') {
            throw new Error('For use in tests only');
        }
        delete StructService.instance;
    }

    public setRestService(restService: RestService) {
        this.restService = restService;
    }

    // for testing
    public setFeatureFlagService(featureFlagService: typeof this.featureFlagService) {
        this.featureFlagService = featureFlagService;
    }

    /**
     * @throws {RequestError}
     * @throws {MalformedDataError}
     * @throws {Error}
     */
    public async getStruct(): Promise<Struct> {
        const json: IResponse<StructDto> = await this.restService.getStruct();
        const {
            tournaments,
            sports,
            outcomes: oddTypes,
            categories,
            marketTree,
        } = json.data;
        const hasEmptyValues: boolean = [
            tournaments,
            sports,
            oddTypes,
            categories,
            marketTree,
        ].some((value) => _isEmpty(value));

        if ((json as any).error || hasEmptyValues) {
            StructService.raiseAndLogMalformedDataError('Struct has empty structures');
        }

        return {
            tournaments: _keyBy(mapTournaments(tournaments), 'id'),
            sports: _keyBy(mapSports(sports), 'id'),
            oddTypes: _keyBy(mapOddTypes(oddTypes), 'id'),
            categories: _keyBy(mapCategories(categories), 'id'),
            marketTree: _keyBy(mapMarketTrees(marketTree), 'sportId'),
            markets: {},
            marketGroupInfos: {},
        };
    }

    private static raiseAndLogMalformedDataError(msg: string) {
        const error = new MalformedDataError(msg);
        logger.error(error);
        throw error;
    }
}

export interface IStructService extends StructService {}

export default StructService as Singleton<StructService>;
